
/*
    Functional utilities exported for reference in different contexts.
*/

const
    config = require('rocketship-config'),
    {
        PRELAUNCH,
        LIVE,
        EXPIRED,
        ANY,

        NAME,
        DATE,

        UNIXTIME,
        MOMENT,
    } = require('./constants'),
    NON_LIVE_PHASES = new Set(
        undefined ||
        [PRELAUNCH, EXPIRED],
    );

/**
 * @param {string|Array<string>} phases - name or names to check, or special values:
 * 'live' which returns true for any phase other than 'pre-launch' or 'expired', and
 * ':any' which returns true no matter the current phase.
 * @param {string} currentPhase - the current active phase, obtained from `getCurrentPhase()`.
 * @returns {boolean?} true if the application is currently in any of the given phases, else
 * `undefined`.
 */
function inPhase (phases, currentPhase) {
    if (typeof phases === 'string') phases = [phases];

    for (const phase of phases) {
        if (
            phase === LIVE && !NON_LIVE_PHASES.has(currentPhase) ||
            phase === ANY ||
            phase === currentPhase
        ) {
            return true;
        }
    }

    return false;
}

/**
 * @param {Array} lifecycle - lifecycle array for a given calendar, obtained from
 * `makeLifecycleArray()`.
 * @param {Object} now - moment instance representing the current time, should respect time-travel.
 * @returns {string} containing the currently active phase name.
 */
function getCurrentPhase (lifecycle, now) {
    let phase = PRELAUNCH;

    for (const l of lifecycle) {
        if (now.diff(l[DATE]) < 0) {
            return phase;
        }
        phase = l[NAME];
    }

    return phase;
}

/**
 * @param {string} phase - name to retrieve from the lifecycle.
 * @param {'moment' | 'unixtime'} [format='moment'] - selects a return value format.
 * @param {Array} lifecycle - lifecycle array for a given calendar, obtained from
 * `makeLifecycleArray()`.
 * @returns {external:moment|number} - depending on `format`, or undefined if `phase` did
 * not exist in the calendar.
 */
function getPhaseStart (phase, format = MOMENT, lifecycle) {
    if (!lifecycle || !lifecycle.length) return;

    const phaseIndex = getPhaseIndex(phase, lifecycle);

    if (phaseIndex !== undefined) {
        const m = phaseIndex === -1 ?
            // Pre-launch
            lifecycle[0][DATE].clone().subtract(1, 'week') :
            // All other phases
            lifecycle[phaseIndex][DATE].clone();

        if (m && format === UNIXTIME) {
            return m.unix();
        }

        // Default format is 'moment'.
        return m;
    }

    return;
}

/**
 * @param {string} phase - name to find.
 * @param {Array} lifecycle - lifecycle array for a given calendar, obtained from
 * `makeLifecycleArray()`.
 * @returns {number?} index of phase in lifecycle array.
 * 'pre-launch' is -1, phases that don't exist are undefined.
 */
function getPhaseIndex (phase, lifecycle) {
    if (phase === PRELAUNCH) {
        return -1;
    }

    const index = lifecycle
    .findIndex(p => p[NAME] === phase);

    if (index === -1) return;
    return index;
}

/**
 * @param {string} from - phase name to start from.
 * @param {number} offset - value added to the index to arrive at the selected phase.
 * @param {Array} lifecycle - lifecycle array for a given calendar, obtained from
 * `makeLifecycleArray()`.
 * @returns {string|void} phase name selected based on `from` and `offset`, or `undefined` if
 * `from` did not exist in the calendar.
 */
function getOffsetPhase (from, offset, lifecycle) {
    const fromIndex = getPhaseIndex(from, lifecycle);

    if (fromIndex === undefined) {
        return;
    }

    const offsetIndex = fromIndex + offset;
    const offsetPhase =  lifecycle[offsetIndex];

    if (offsetPhase) {
        return offsetPhase[NAME];
    }
    else if (offsetIndex === -1) {
        return PRELAUNCH;
    }

    return;
}

exports.inPhase = inPhase;
exports.getCurrentPhase = getCurrentPhase;
exports.getPhaseStart = getPhaseStart;
exports.getPhaseIndex = getPhaseIndex;
exports.getOffsetPhase = getOffsetPhase;
